import { Dialog } from '@plato/ui/containers/dialog';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { useEffect, type ReactElement } from 'react';
import { Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { WarningIcon } from '../../../assets/icons/warning-icon.tsx';
import { Notice } from '../../../components/ui/notice.tsx';
import Text from '../../../components/ui/text.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useFormExtended } from '../../../lib/hooks/use-form-extended.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { SearchEntity } from '../../../server/types/search-types.ts';
import { api } from '../../../utils/api.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { TransactionCreationStartedEventSource } from '../../analytics/types/analytics-events.ts';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import type { DialogInnerProps } from '../../dialog/types.ts';
import { EntitySearchCombobox } from '../../shared/components/form-items-with-fetch/entity-search-combobox.tsx';
import { DraftTypeOption } from '../components/draft-type-option.tsx';
import { StorageLocationInput } from '../form/storage-location-input.tsx';
import type { TransactionType } from '../hooks/use-draft-form.ts';

export type DraftOrderCreateDialogProps = {
    customerId?: string;
    draftType?: TransactionType;
    source: TransactionCreationStartedEventSource;
};

export const DraftOrderCreateDialog = DialogStateManager.create(DraftOrderCreateDialogInner);

type DraftOrderCreateDialogInnerProps = DraftOrderCreateDialogProps & DialogInnerProps;

type CreateDraftFormValues = {
    customer_id: string;
    storage_id: string;
    transaction_type: TransactionType;
};

function DraftOrderCreateDialogInner({
    customerId,
    draftType,
    source,
}: DraftOrderCreateDialogInnerProps): ReactElement {
    const {
        canReadTransactionStorageLocation,
        canUpdateTransactionStorageLocation,
        transactionDraftAvailability,
        transactionDraftTypes = ['ORDER', 'QUOTE'],
    } = useAppConfig();
    const isErpUnreachable = transactionDraftAvailability === 'CURRENTLY_UNAVAILABLE';
    const { remove, visible } = useDialog();
    const { t } = useLocale();
    const { toast } = useToast();
    const sendEvent = useAnalytics();
    const navigate = useNavigate();

    const selectedDraftType =
        draftType && transactionDraftTypes.includes(draftType)
            ? draftType
            : (transactionDraftTypes[0] ?? 'ORDER');

    const { control, handleSubmit, reset, watch } = useFormExtended({
        defaultValues: {
            customer_id: customerId ?? '',
            storage_id: '',
            transaction_type: selectedDraftType,
        },
    });

    useEffect(() => {
        sendEvent('transaction_creation_started', { customer_id: customerId, source });
    }, [sendEvent, customerId, source]);

    const selectedCustomerId = watch('customer_id');
    const selectedType = watch('transaction_type');

    const { data: selectedCustomer } = api.platoUser.getDetailCustomer.getBasics.useQuery(
        { erpCustomerId: selectedCustomerId },
        { enabled: !!selectedCustomerId },
    );

    const {
        data: defaults,
        isLoading: isLoadingGetDefaults,
        isError: isErrorGetDefaults,
    } = api.platoUser.query.salesDraft.getSalesDraft.getDefaultHeader.useQuery(
        { customer_id: selectedCustomerId, sales_order_type: selectedType },
        { enabled: !!selectedCustomerId },
    );

    useEffect(() => {
        if (!defaults) {
            return;
        }
        reset(
            {
                customer_id: selectedCustomerId,
                storage_id: defaults.storage_id,
                transaction_type: selectedType,
            },
            { keepDirtyValues: true },
        );
    }, [defaults, reset, selectedCustomerId, selectedType]);

    const { mutate, isPending } = api.platoUser.command.salesDraft.draft.create.useMutation({
        onSuccess: async (data) => {
            await navigate(`/drafts/${encodeURIComponent(data.draft_order.draft_order_id)}/line-items`);
            remove();
        },
        onError: (error) => {
            toast({
                title: 'error',
                description: error.message,
                icon: 'warning',
            });
        },
    });

    function onSubmit({ transaction_type, ...values }: CreateDraftFormValues): void {
        if (!defaults) {
            return;
        }

        mutate({
            draft_order_id: uuidv4(),
            ...defaults,
            ...values,
            type: transaction_type,
        });
    }

    // Only count defaults as loading when the query is actually enabled (selectedCustomerId is set).
    // TanStack Query v4 returns isLoading=true for disabled queries with no cached data.
    const isLoading = (!!selectedCustomerId && isLoadingGetDefaults) || isPending;

    return (
        <Dialog open={visible} onClose={remove} disablePointerDismissal>
            <DialogForm className="sm:max-w-2xl">
                <DialogForm.Header title={t('sales_draft__new_form___title')} />
                {isErpUnreachable && (
                    <div className="px-4 py-3">
                        <Notice
                            variant="warning"
                            title={t('erp_connection_error_banner___title')}
                            icon={WarningIcon}
                            description={
                                <Text className="block">
                                    {t('erp_connection_error_banner___description')}
                                </Text>
                            }
                        />
                    </div>
                )}
                <DialogForm.Content>
                    <DialogForm.ItemList col>
                        {transactionDraftTypes.length > 1 && (
                            <DialogForm.Item>
                                <DialogForm.Label>{t('sales_draft__new_form___type')}</DialogForm.Label>
                                <DialogForm.Body>
                                    <Controller
                                        control={control}
                                        name="transaction_type"
                                        render={({ field: { value, onChange } }) => (
                                            <RadioGroup.Root
                                                className="flex w-full gap-3"
                                                value={value}
                                                onValueChange={onChange}
                                            >
                                                {transactionDraftTypes.map((type) => (
                                                    <DraftTypeOption
                                                        key={type}
                                                        value={type}
                                                        label={
                                                            type === 'ORDER'
                                                                ? t('sales_draft__new_form___order')
                                                                : t('sales_draft__new_form___quote')
                                                        }
                                                    />
                                                ))}
                                            </RadioGroup.Root>
                                        )}
                                    />
                                </DialogForm.Body>
                            </DialogForm.Item>
                        )}
                        <DialogForm.Item>
                            <DialogForm.Label>{t('sales_draft__new_form___customer')}</DialogForm.Label>
                            <Controller
                                name="customer_id"
                                control={control}
                                rules={{ required: true }}
                                render={({ field: { onChange }, fieldState: { error } }) => {
                                    return (
                                        <>
                                            <DialogForm.Body>
                                                <EntitySearchCombobox
                                                    className="w-full"
                                                    entities={[SearchEntity.Customer]}
                                                    disabled={transactionDraftAvailability !== 'AVAILABLE'}
                                                    defaultValue={
                                                        selectedCustomer
                                                            ? [
                                                                  {
                                                                      id: selectedCustomer.customerId,
                                                                      name:
                                                                          selectedCustomer.customerName ?? '',
                                                                      entity: SearchEntity.Customer,
                                                                  },
                                                              ]
                                                            : undefined
                                                    }
                                                    inputPlaceholder={t('placeholder__select_customer')}
                                                    onValueChange={(value) => {
                                                        const [item] = value;
                                                        onChange(item?.id ?? '');
                                                    }}
                                                    tracking={{
                                                        context: 'draft_customer_select',
                                                        context_entity_type: 'draft',
                                                    }}
                                                />
                                            </DialogForm.Body>
                                            <DialogForm.Error error={error} />
                                        </>
                                    );
                                }}
                            />
                        </DialogForm.Item>
                        {/**
                         * OK here it is tricky, we need to check if the user has the permission to read the storage_id field
                         * because eBootis does not use this field to submit the sales draft
                         */}
                        {canReadTransactionStorageLocation && (
                            <DialogForm.Item>
                                <DialogForm.Label>
                                    {t('sales_draft__new_form___storage_location')}
                                </DialogForm.Label>
                                <DialogForm.Body>
                                    <Controller
                                        control={control}
                                        name="storage_id"
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <StorageLocationInput
                                                className="w-full"
                                                disabled={!canUpdateTransactionStorageLocation}
                                                onChange={field.onChange}
                                                placeholder={t('placeholder__select_storage')}
                                                value={field.value}
                                            />
                                        )}
                                    />
                                </DialogForm.Body>
                            </DialogForm.Item>
                        )}

                        {isErrorGetDefaults && <DialogForm.Error error={t('writeback_error')} />}
                    </DialogForm.ItemList>
                </DialogForm.Content>
                <DialogForm.SubmitBlock
                    disabled={isLoading || isErrorGetDefaults || transactionDraftAvailability !== 'AVAILABLE'}
                    isLoading={isLoading}
                    onCancel={remove}
                    onSubmit={handleSubmit(onSubmit)}
                />
            </DialogForm>
        </Dialog>
    );
}
